// src/services/reminder_api.js
//
// Admin-only client for the reminder schedule (see Part F of
// docs/WEEK_INTERVAL_AND_WEEK_START.md in the API repo).
//
// The backend endpoints do not exist yet; reminder_mock.js serves them. When
// the backend lands: set USE_MOCK to false and delete reminder_mock.js.

import { apiClient } from "./api_client";
import reminderMock from "./reminder_mock";

// Both endpoints exist now. The flags stay as a deliberate switch for working
// against a backend that predates them; nothing reaches reminder_mock unless one
// is flipped on purpose.
const USE_MOCK_READ = false;
const USE_MOCK_WRITE = false;

// Exported so the panel can say so on screen. Mock data that cannot be
// identified as mock data is worse than no data.
export const reminderIsMocked = USE_MOCK_READ || USE_MOCK_WRITE;

// Message types and what actually decides whether each one fires.
//
// `hint` is the purpose; `logic` is the real gate, transcribed from the helper
// named in `source` (captain_api/celery/tasks.py). The distinction matters most
// for the daily ones: the schedule below controls when the check RUNS, not how
// often a participant is messaged. "Checked every day" reads as "texted every
// day" unless the actual condition is spelled out next to it.
//
// Keep these in step with the helpers. If a threshold changes in tasks.py and
// not here, this page becomes confidently wrong, which is worse than silent.
export const CHANNELS = [
  { key: "sms", label: "SMS" },
  { key: "email", label: "Email" },
];

export const MESSAGE_TYPES = [
  {
    key: "new_session_available",
    label: "New session available",
    trigger: "block_relative",
    hint: "Announces that the next session has opened.",
    logic:
      "Sent to every participant on the day the period opens. No condition beyond the date.",
    source: "new_session_available()",
  },
  {
    key: "session_not_started",
    label: "Session not started",
    trigger: "block_relative",
    hint: "Nudges participants who have not begun the current period's session.",
    logic:
      "Sent only to participants with no session dated on or after the start of the current period. Anyone who has already started is skipped.",
    source: "new_session_available()",
  },
  {
    key: "session_not_finished",
    label: "Session not finished",
    trigger: "daily",
    hint: "Catches a session that was opened but never completed.",
    logic:
      "Checked daily, but sent at most once. The participant's latest session must still be open (its last message carries no end flag), and the session must be between 24 and 48 hours old — 72 to 96 hours on a 2-week cadence. That 24-hour window is what stops it repeating on later days.",
    source: "session_not_finished()",
  },
  {
    key: "implementation_reminder",
    label: "Implementation reminder",
    trigger: "daily",
    hint: "Reminds a participant of the actions they committed to.",
    logic:
      "Checked daily, sent rarely. Requires the latest session to have ended, to sit inside the current period, and to be at least 72 hours old — 120 hours on a 2-week cadence. Suppressed if any other message reached the participant within that same window, so it cannot stack on top of another reminder.",
    source: "implementation_reminder()",
    // The only message whose body is generated per participant: send_sms and
    // send_email run the group's sms_message / email_message model rather than
    // filling a template. Scheduling both channels in the same slot therefore
    // doubles concurrent LLM calls, which is the contention that already forced
    // batch summarisation to run sequentially on local families.
    llmGenerated: true,
  },
];

// Shown on the page but not editable here. Onboarding is keyed to a
// participant's enrolment state rather than to a session period, so it has no
// meaningful day-offset and stays on a fixed cron in message.toml.
export const FIXED_MESSAGE_TYPES = [
  {
    key: "onboarding_reminder",
    label: "Onboarding reminder",
    hint: "Prompts participants who have not finished setting up their account.",
    logic:
      "Sent to active participants whose phone number is still unconfirmed. Not tied to the session period, so it does not follow a cadence plan.",
    source: "send_onboarding_reminder()",
  },
];

export function messageMeta(key) {
  return (
    MESSAGE_TYPES.find((m) => m.key === key) ||
    FIXED_MESSAGE_TYPES.find((m) => m.key === key) ||
    null
  );
}

const reminderApi = {
  // GET /message/schedule — both plans plus the dispatcher's own constants,
  // so the page can explain the timing instead of hardcoding it.
  // There is deliberately NO fallback to reminder_mock here. The mock's seed is
  // identical to the migration's, so falling back rendered a page that looked
  // completely genuine while showing local fiction -- and since save() has no
  // such fallback, the failure surfaced later as an inexplicable 404 on Save.
  // An error the admin can see is worth more than a plausible-looking page.
  list: async () => {
    if (USE_MOCK_READ) return reminderMock.list();
    const { data } = await apiClient.get("/message/schedule");
    // A non-admin gets { message } with no plans, same as the rest of the admin
    // API. Surface it rather than rendering an empty page.
    if (!data?.plans?.length) {
      throw new Error(data?.message || "The server returned no reminder plans.");
    }
    return data;
  },

  // PUT /message/schedule/{interval_weeks} — replaces one plan. Mutable by
  // design; see the note at the top of reminder_mock.js for why this is not
  // append-only the way group_cadence is.
  save: async (intervalWeeks, rows) => {
    if (USE_MOCK_WRITE) return reminderMock.save(intervalWeeks, rows);
    // Send only the fields the endpoint owns. Rows carry id, timestamps and
    // updated_by once they come back from a read, and echoing those would ask
    // the server to trust client-supplied audit data.
    const body = rows.map((r) => ({
      message_title: r.message_title,
      channel: r.channel,
      trigger_type: r.trigger_type,
      day_offset: r.day_offset,
      send_time: r.send_time,
      enabled: r.enabled,
    }));
    const { data } = await apiClient.put(`/message/schedule/${intervalWeeks}`, {
      rows: body,
    });
    // The endpoint returns { message } for a rejected plan rather than raising,
    // matching the rest of the admin API, so surface that as an error here.
    if (data?.message) throw new Error(data.message);
    return data;
  },
};

export default reminderApi;
